import { useMutation, useQueryCache } from "@pinia/colada";
import { SpaceUpdate, useApi } from "@soft-db/monitoring-api-client";
import { usePermissionStore } from "../stores/permission";
import { useScopeStore } from "../stores/scope";
import { QueryOptions, useQueryEnabled, useScopedQuery } from "./query";
import { reportsKeys } from "./report-query";
import { sitesKeys } from "./site-query";
import { stationsKeys } from "./station-query";

// --- Keys ---

export const spacesKeys = {
	root: ["spaces"] as const,

	list: () => [...spacesKeys.root, "list"] as const,

	byContext: () => [...spacesKeys.list(), "context"] as const,

	byDist: (dist: string) => [...spacesKeys.list(), "dist", dist] as const,

	byId: (id: string | null) => [...spacesKeys.root, "id", id] as const,
};

// --- Cache effects ---

function useUpdateCacheById() {
	const cache = useQueryCache();

	return (_: any, { space }: { space: string }) => {
		cache.invalidateQueries({ key: spacesKeys.byId(space) });
	};
}

function useInvalidateSpaceSite() {
	const cache = useQueryCache();

	return (_: any, { space }: { space: string }) => {
		cache.invalidateQueries({ key: sitesKeys.bySpace(space) });
	};
}

function useInvalidateSpaceStation() {
	const cache = useQueryCache();

	return (_: any, { space }: { space: string }) => {
		cache.invalidateQueries({ key: stationsKeys.bySpace(space) });
	};
}

function useInvalidateListCache() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: spacesKeys.list() });
	};
}

// --- Queries ---

function useSpaceEnabled(...args: MaybeRefOrGetter<string | boolean | null>[]) {
	const { hasPermission } = usePermissionStore();

	return useQueryEnabled(...args, () => hasPermission("space.get"));
}

export function useSpace(id: MaybeRefOrGetter<string | null>) {
	const api = useApi().space();

	const { data: space, ...rest } = useScopedQuery({
		key: () => spacesKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)!),
		enabled: useSpaceEnabled(id),
	});

	return {
		space,
		data: space,
		...rest,
	};
}

export function useSpaces(opts?: QueryOptions) {
	const api = useApi().space();

	const scopeStore = useScopeStore();

	const { data: spaces, ...rest } = useScopedQuery({
		key: () => spacesKeys.byContext(),
		query: () => api.fetchContext(),
		enabled: useSpaceEnabled(opts?.enabled || true, () => scopeStore.isScope("distributor")),
	});

	return {
		spaces,
		data: spaces,
		...rest,
	};
}

export function useCreateSpace() {
	const api = useApi().space();

	return useMutation({
		mutation: ({ dist, space }: { dist: string; space: SpaceUpdate }) =>
			api.createSpace(dist, space),
		onSuccess: useInvalidateListCache(),
	});
}

export function useUpdateSpace() {
	const api = useApi().space();

	return useMutation({
		mutation: ({ space, update }: { space: string; update: SpaceUpdate }) =>
			api.updateSpace(space, update),
		onSuccess: useUpdateCacheById(),
	});
}

export function useUpdateSpaceLogo() {
	const api = useApi().space();

	return useMutation({
		mutation: ({ space, image }: { space: string; image: File }) => api.updateLogo(space, image),
		onSuccess: useUpdateCacheById(),
	});
}

export function useDeleteSpaceLogo() {
	const api = useApi().space();

	return useMutation({
		mutation: ({ space }: { space: string }) => api.deleteLogo(space),
		onSuccess: useUpdateCacheById(),
	});
}

export function useAddSiteToSpace() {
	const api = useApi().space();

	return useMutation({
		mutation: ({ space, site, owner }: { space: string; site: string; owner: boolean }) =>
			api.addSite(space, site, owner),
		onSuccess: useInvalidateSpaceSite(),
	});
}

export function useRemoveSiteFromSpace() {
	const api = useApi().space();

	const cache = useQueryCache();
	const invalidateSpaceSite = useInvalidateSpaceSite();

	return useMutation({
		mutation: ({ space, site }: { space: string; site: string }) => api.deleteSite(space, site),
		onSuccess: (_, { space }) => {
			invalidateSpaceSite(_, { space });

			cache.invalidateQueries({ key: sitesKeys.byPublicPage("").slice(0, -1) });
		},
	});
}

export function useAddStationToSpace() {
	const api = useApi().space();

	const cache = useQueryCache();
	const invalidateSpaceStation = useInvalidateSpaceStation();

	return useMutation({
		mutation: ({ space, station }: { space: string; station: string }) =>
			api.addStation(space, station),
		onSuccess: (_, { space }) => {
			invalidateSpaceStation(_, { space });
			cache.invalidateQueries({ key: stationsKeys.root });
		},
	});
}

export function useRemoveStationFromSpace() {
	const api = useApi().space();

	const invalidateSpaceStation = useInvalidateSpaceStation();

	return useMutation({
		mutation: ({ space, station }: { space: string; station: string }) =>
			api.deleteStation(space, station),
		onSuccess: (_, { space }) => {
			invalidateSpaceStation(_, { space });
		},
	});
}

export function useAddReportToSpace() {
	const api = useApi().space();

	const cache = useQueryCache();
	return useMutation({
		mutation: ({ space, report }: { space: string; report: string }) =>
			api.addReport(space, report),
		onSuccess: (_, { space, report }) => {
			cache.invalidateQueries({ key: reportsKeys.bySpace(space) });
			cache.invalidateQueries({ key: reportsKeys.byId(report) });
		},
	});
}

export function useRemoveReportFromSpace() {
	const api = useApi().space();

	const cache = useQueryCache();
	return useMutation({
		mutation: ({ space, report }: { space: string; report: string }) =>
			api.deleteReport(space, report),
		onSuccess: (_, { space, report }) => {
			cache.invalidateQueries({ key: reportsKeys.bySpace(space) });
			cache.invalidateQueries({ key: reportsKeys.byId(report) });
		},
	});
}
